import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        success:
          "border-transparent bg-success text-success-foreground hover:bg-success/80",
        warning:
          "border-transparent bg-warning text-warning-foreground hover:bg-warning/80",
        outline: "text-foreground",
        // Stage/status colors
        gray: "border-gray-200 bg-gray-100 text-gray-800",
        red: "border-red-200 bg-red-100 text-red-800",
        orange: "border-orange-200 bg-orange-100 text-orange-800",
        yellow: "border-yellow-200 bg-yellow-100 text-yellow-800",
        green: "border-green-200 bg-green-100 text-green-800",
        blue: "border-blue-200 bg-blue-100 text-blue-800",
        indigo: "border-indigo-200 bg-indigo-100 text-indigo-800",
        purple: "border-purple-200 bg-purple-100 text-purple-800",
        pink: "border-pink-200 bg-pink-100 text-pink-800",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

type BadgeColor = "gray" | "red" | "orange" | "yellow" | "green" | "blue" | "indigo" | "purple" | "pink";

export interface BadgeProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color'>,
    VariantProps<typeof badgeVariants> {
  /** Color variant for status badges */
  color?: BadgeColor | (string & NonNullable<unknown>);
}

function Badge({ className, variant, color, ...props }: BadgeProps) {
  // Support color prop by mapping to variant
  const effectiveVariant = (color || variant) as VariantProps<typeof badgeVariants>['variant'];
  return (
    <div className={cn(badgeVariants({ variant: effectiveVariant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
